import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import OnboardingHeader from "./OnboardingHeader";
import StepProgressBar from "./StepProgressBar";
import OnboardingCard from "./OnboardingCard";
import OnboardingInput from "./OnboardingInput";
import OnboardingButton from "./OnboardingButton";
import { ReactComponent as CallIcon } from "../../assets/svgs/call.svg";
import { ReactComponent as NextIcon } from "../../assets/svgs/next.svg";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { updatePurlUser } from "../../store/reducers/purlUserReducer";
import { updateStage } from "../../store/reducers/stageReducer";
import { useUpdateProfileMutation } from "../../services/api";
import { showNotification } from "../../services/showNotification";
import { getStartedSchema } from "../../yup";
import useHasPermission from "../../hooks/useHasPermission";

interface IGetStartedForm {
  phone: string;
  firstName: string;
  lastName: string;
}

// This screen's step in the onboarding flow (see ONBOARDING_STEPS).
const STEP_KEY = "profileVerificationCompleted" as const;

const GetStarted = () => {
  const user = useAppSelector((state) => state.purlUser);
  const dispatch = useAppDispatch();
  
  const { hasPermission } = useHasPermission();

  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IGetStartedForm>({
    resolver: yupResolver(getStartedSchema),
    mode: "onChange",
    defaultValues: { 
      phone: "", 
      firstName: "", 
      lastName: "" 
    },
  });

  useEffect(() => {
    reset({ 
      phone: user.phone ?? "",
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
    });
  }, [user.phone, user.firstName, user.lastName, reset]);

  const onSubmit: SubmitHandler<IGetStartedForm> = async ({ phone, firstName, lastName }) => {
    const mobile = phone.replace(/\D/g, "");
    try {
      const res = await updateProfile({ mobile, firstName, lastName }).unwrap();
      dispatch(updatePurlUser({ 
        phone: res.data.mobile,
        firstName: res.data.firstName,
        lastName: res.data.lastName
      }));
      dispatch(updateStage({ [STEP_KEY]: true }));
    } catch {
      showNotification("error", "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <OnboardingHeader />

      <main className="w-full max-w-5xl mx-auto p-10">
        <StepProgressBar stepKey={STEP_KEY} />

        <OnboardingCard className="p-8 sm:p-10 mt-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
            <div className="mb-12 mt-4 text-left">
              <h2 className="text-left text-3xl font-bold text-[#030724] font-['Proxima_Nova']">
                Let's Get Started!
              </h2>
              <p className="text-xl font-normal text-[#46464D] mt-2 font-['Proxima_Nova']">
                Please make sure your legal name and phone number are correct.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <OnboardingInput
                  label="Legal First Name"
                  placeholder="Legal first name"
                  error={errors.firstName?.message}
                  disabled={!hasPermission("editFirstName")}
                  {...register("firstName")}
                />
              </div>
              <div className="flex-1">
                <OnboardingInput
                  label="Legal Last Name"
                  placeholder="Legal last name"
                  error={errors.lastName?.message}
                  disabled={!hasPermission("editLastName")}
                  {...register("lastName")}
                />
              </div>
            </div>

            <div className="mb-8">
              <OnboardingInput
                label="Mobile Phone Number"
                placeholder="(555) 000-0000"
                type="tel"
                icon={<CallIcon />}
                error={errors.phone?.message}
                disabled={!hasPermission("editMobileNumber")}
                {...register("phone")}
              />
            </div>

            <OnboardingButton
              title="CONTINUE TO VERIFICATION"
              type="submit"
              loading={isUpdating}
              disabled={isUpdating}
              icon={<NextIcon/>}
            />
          </form>
        </OnboardingCard>
      </main>
    </div>
  );
};

export default GetStarted;
